import { useCallback, useEffect, useState } from "react";
import { getDataClient } from "@/lib/dataClient";
import {useAiCoach} from "@/hooks/useAiCoach";

interface WeakTopic {
  subject: string;
  topic: string;
  mastery_percent: number;
}

interface PlanDay {
  day: string;
  subject: string;
  topic: string;
  mastery: number | null;
  minutes: number;
  questions: number;
}

const DAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

export default function HaftalikPlanPage() {
  const { openCoach } = useAiCoach();
  const [plan, setPlan] = useState<PlanDay[]>([]);
  const [aiSummary, setAiSummary] = useState<string>("");
  const [strongSubjects, setStrongSubjects] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dataClient = getDataClient();
      const { data: authData } = await dataClient.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) {
        setLoading(false);
        return;
      }

      const [weakRes, profileRes] = await Promise.all([
        dataClient
          .from("weak_topics")
          .select("subject, topic, mastery_percent")
          .eq("user_id", userId)
          .order("mastery_percent", { ascending: true })
          .limit(7),
        dataClient
          .from("student_ai_profile")
          .select("ai_summary, daily_goal, weekly_goal, strong_subjects")
          .eq("user_id", userId)
          .maybeSingle(),
      ]);

      if (weakRes.error) throw weakRes.error;
      if (profileRes.error) throw profileRes.error;

      const weakTopics = (weakRes.data ?? []) as WeakTopic[];
      const profile = profileRes.data as
        | { ai_summary?: string | null; daily_goal?: number | null; weekly_goal?: number | null; strong_subjects?: unknown }
        | null;

      setAiSummary(profile?.ai_summary ?? "");
      const strong = Array.isArray(profile?.strong_subjects)
        ? profile.strong_subjects.filter((s): s is string => typeof s === "string")
        : [];
      setStrongSubjects(strong);

      const dailyQuestions = profile?.daily_goal && profile.daily_goal > 0 ? profile.daily_goal : 20;
      const dailyMinutes =
        profile?.weekly_goal && profile.weekly_goal > 0
          ? Math.round(profile.weekly_goal / 7)
          : 60;

      const generated: PlanDay[] = DAYS.map((day, i) => {
        const topic = weakTopics[i % weakTopics.length];
        return {
          day,
          subject: topic?.subject ?? "Genel Tekrar",
          topic: topic?.topic ?? "Serbest Çalışma",
          mastery: topic?.mastery_percent ?? null,
          minutes: dailyMinutes,
          questions: dailyQuestions,
        };
      });

      setPlan(generated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Plan yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            Haftalık Çalışma Planı
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Zayıf konularına göre sana özel hazırlanmış haftalık plan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openCoach("Bana bu hafta için kişisel bir çalışma planı hazırlar mısın?")}
            className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors"
          >
            <i className="ri-sparkling-2-line mr-1.5" aria-hidden="true"></i>
            AI Koça Sor
          </button>
          <button
            type="button"
            onClick={load}
            className="whitespace-nowrap px-4 py-2 rounded-md border border-background-200 text-foreground-700 text-sm font-semibold hover:bg-background-100 transition-colors"
          >
            <i className="ri-refresh-line mr-1.5" aria-hidden="true"></i>
            Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <i className="ri-error-warning-line text-accent-700 text-xl" aria-hidden="true"></i>
            <p className="text-sm text-foreground-900">{error}</p>
          </div>
          <button
            type="button"
            onClick={load}
            className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      )}

      {/* AI Özeti */}
      {aiSummary && (
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-5 flex gap-3">
          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 shrink-0">
            <i className="ri-brain-line text-lg" aria-hidden="true"></i>
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground-950">AI Koç Özeti</p>
            <p className="mt-1 text-sm text-foreground-800 leading-relaxed">{aiSummary}</p>
          </div>
        </div>
      )}

      {/* Güçlü yönler */}
      {strongSubjects.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-foreground-600">Güçlü derslerin:</span>
          {strongSubjects.map((s) => (
            <span
              key={s}
              className="whitespace-nowrap px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Plan */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-40 rounded-lg bg-background-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {plan.map((day, i) => (
            <div key={day.day} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold">
                  {i + 1}
                </span>
                <span className="text-xs font-medium text-foreground-500">{day.day}</span>
              </div>
              <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
                {day.topic}
              </h3>
              <p className="mt-0.5 text-sm text-foreground-600">{day.subject}</p>

              <div className="mt-4 pt-4 border-t border-background-100 flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-foreground-600">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {day.minutes} dk
                </span>
                <span className="flex items-center gap-1.5 text-foreground-600">
                  <i className="ri-list-check-2" aria-hidden="true"></i>
                  {day.questions} soru
                </span>
              </div>

              {day.mastery !== null && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>Konu hakimiyeti</span>
                    <span>%{day.mastery}</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-background-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent-500"
                      style={{ width: `${day.mastery}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && plan.length === 0 && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-10 text-center">
          <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-calendar-todo-line text-2xl" aria-hidden="true"></i>
          </span>
          <p className="mt-4 text-base font-semibold text-foreground-950">
            Henüz plan oluşturacak yeterli veri yok
          </p>
          <p className="mt-1 text-sm text-foreground-600">
            Soru bankasından soru çözüp deneme sınavına girince, zayıf konuların burada plana dönüşecek.
          </p>
        </div>
      )}
    </div>
  );
}